import { Alert, Button, Stack, TextField, Typography } from "@mui/material";
import { useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import useApplyErrorToForm from "~/modules/app/hooks/useApplyErrorToForm";

import useRecoverPassword, { RecoverPasswordInput } from "../../api/useRecoverPassword";

export default function ResetPasswordForm() {
    const { t } = useTranslation("auth");
    const { recoverPassword, data, error } = useRecoverPassword();
    const {
        handleSubmit,
        register,
        setError,
        formState: { errors, isLoading, isDirty },
    } = useForm<RecoverPasswordInput>({
        defaultValues: {
            email: undefined,
        },
    });

    const [submittedEmail, setSubmittedEmail] = useState<string | undefined>();

    const onSubmit: SubmitHandler<RecoverPasswordInput> = async (email: RecoverPasswordInput) => {
        await recoverPassword(email);
        setSubmittedEmail(email.email);
    };

    const navigate = useNavigate();

    useApplyErrorToForm(error, setError);

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            {!data && (
                <>
                    <Stack gap={2}>
                        {errors.root && (
                            <Alert severity="error">
                                <Typography>{errors.root.message}</Typography>
                            </Alert>
                        )}

                        <TextField
                            label={t("resetPasswordForm.labelEmail")}
                            error={!!errors.email}
                            helperText={errors.email?.message}
                            {...register("email", {
                                required: t("resetPasswordForm.labelEmailRequired"),
                            })}
                        />

                        <Button
                            type="submit"
                            color="primary"
                            variant="contained"
                            disabled={!isDirty}
                            loading={isLoading}
                        >
                            {t("resetPasswordForm.btnSubmit")}
                        </Button>
                    </Stack>
                </>
            )}

            {data && (
                <>
                    <Typography mb={5} variant="body1">
                        {t("resetPasswordForm.success", { email: submittedEmail })}
                    </Typography>
                    <Button variant="text" onClick={() => navigate("/login")}>
                        {t("resetPasswordForm.btnBack")}
                    </Button>
                </>
            )}
        </form>
    );
}
